import {
    collection,
    doc,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    addDoc,
    query,
    where,
    serverTimestamp,
    orderBy,
    limit
} from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { authService, User } from './authService';

export interface AdminLog {
    id?: string;
    adminId: string;
    adminName: string;
    action: string;
    targetUserId?: string;
    details: string;
    timestamp: any;
}

export const adminService = {
    // --- Audit Logging ---
    async logAction(action: string, details: string, targetUserId?: string) {
        const admin = authService.getCurrentUser();
        if (!admin) return;

        try {
            await addDoc(collection(db, 'admin_logs'), {
                adminId: admin.id,
                adminName: admin.name,
                action,
                details,
                targetUserId: targetUserId || null,
                timestamp: serverTimestamp()
            });
        } catch (error) {
            console.error('Failed to log admin action', error);
        }
    },

    async getAuditLogs(limitCount = 100) {
        try {
            const q = query(
                collection(db, 'admin_logs'),
                orderBy('timestamp', 'desc'),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AdminLog));
        } catch (error) {
            console.error('Error fetching audit logs', error);
            throw new Error('Could not fetch logs');
        }
    },

    // --- User Management ---
    async getAllUsers() {
        try {
            const snapshot = await getDocs(collection(db, 'users'));
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
        } catch (error) {
            console.error('Error fetching users', error);
            return [];
        }
    },

    async suspendUser(userId: string, reason: string) {
        try {
            await updateDoc(doc(db, 'users', userId), {
                isSuspended: true,
                suspensionReason: reason,
                suspendedAt: serverTimestamp()
            });
            await this.logAction('SUSPEND_USER', `Suspended user: ${reason}`, userId);
        } catch (error) {
            console.error('Error suspending user', error);
            throw new Error('Failed to suspend user');
        }
    },

    async unsuspendUser(userId: string) {
        try {
            await updateDoc(doc(db, 'users', userId), {
                isSuspended: false,
                suspensionReason: null,
                suspendedAt: null
            });
            await this.logAction('UNSUSPEND_USER', 'Unsuspended user', userId);
        } catch (error) {
            console.error('Error unsuspending user', error);
            throw new Error('Failed to unsuspend user');
        }
    },

    async grantSubscription(userId: string, type: '7_day' | '30_day' | 'lifetime') {
        let durationDays = 0;
        let label = '';

        switch (type) {
            case '7_day': durationDays = 7; label = '7 Day Free'; break;
            case '30_day': durationDays = 30; label = '30 Day Free'; break;
            case 'lifetime': durationDays = 36500; label = 'Lifetime'; break;
        }

        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + durationDays);

        try {
            await updateDoc(doc(db, 'users', userId), {
                subscriptionStatus: 'active',
                subscriptionType: 'gift',
                subscriptionLabel: label,
                subscriptionExpiry: expiryDate.getTime()
            });
            await this.logAction('GRANT_SUB', `Granted ${label} subscription`, userId);
        } catch (error) {
            console.error('Error granting subscription', error);
            throw new Error('Failed to grant subscription');
        }
    },

    /**
     * Grant a permanent free account (no expiry).
     */
    async grantFreeAccount(userId: string) {
        try {
            await updateDoc(doc(db, 'users', userId), {
                subscriptionStatus: 'active',
                subscriptionType: 'gift',
                subscriptionLabel: 'Free Account',
                subscriptionExpiry: Date.now() + (365 * 24 * 60 * 60 * 1000 * 100) // ~100 years
            });
            await this.logAction('GRANT_FREE', 'Granted permanent free account', userId);
        } catch (error) {
            console.error('Error granting free account', error);
            throw new Error('Failed to grant free account');
        }
    },

    /**
     * Send a password reset email to a user via Cloud Function.
     */
    async sendPasswordReset(email: string) {
        try {
            const adminSendPasswordReset = httpsCallable(functions, 'adminSendPasswordReset');
            await adminSendPasswordReset({ email });
            await this.logAction('PASSWORD_RESET', `Sent password reset to ${email}`);
        } catch (error) {
            console.error('Error sending password reset', error);
            throw new Error('Failed to send password reset');
        }
    },

    /**
     * Delete a user account via Cloud Function (removes from Auth + Firestore).
     */
    async deleteUser(userId: string) {
        try {
            const adminDeleteUser = httpsCallable(functions, 'adminDeleteUser');
            await adminDeleteUser({ userId });
            // Cloud Function handles the audit log
        } catch (error) {
            console.error('Error deleting user', error);
            throw new Error('Failed to delete user');
        }
    }
};

